import React, { useContext, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilePlus2, Check, X, ExternalLink } from 'lucide-react';
import { axios, auth, mbUrl, MbContext, muted, primary, runCall, rm } from './mbApi';
import { STAGE_LABEL, usePicName } from './HandoffControl';

export const SUGG_STATUS = { draf: 'Draf', diterima: 'Diterima', ditolak: 'Ditolak' };
const STATUS_CLS = { draf: 'bg-white/10', diterima: 'bg-emerald-500/15 text-emerald-400', ditolak: 'bg-red-500/15 text-red-400' };
const TYPE_LABEL = { variasi_terkawal: 'Variasi terkawal (ubah satu unsur)', konsep_baharu: 'Konsep baharu (pakej berubah)' };
const PATH_LABEL = { ai_self: 'Hasilkan guna AI', creative_team: 'Minta Creative Team' };

const Sec = ({ title, children }) => (
  <div>
    <p className={`text-xs font-semibold uppercase tracking-wider ${primary}`}>{title}</p>
    <div className={`text-sm ${muted}`}>{children}</div>
  </div>
);
const Bullets = ({ items }) => <ul className="list-disc pl-4">{(items || []).map((x) => <li key={x}>{x}</li>)}</ul>;

export const Evidence = ({ snap, baseline }) => (
  <span data-testid="mb-sugg-evidence">
    Spend {rm(snap?.amount_spent_myr)} · {snap?.joins_first ?? 0} join unik ({snap?.joins_rejoin ?? 0} rejoin, {snap?.joins ?? 0} semua) ·
    CPS unik {rm(snap?.cps_first_myr)} · CPS semua {rm(snap?.cps_myr)} · {snap?.days ?? 0} hari ({snap?.date_from || '—'} → {snap?.date_to || '—'})
    {snap?.includes_today ? ' · termasuk hari ini (separa)' : ''} · {snap?.tests ?? 0} ujian / {snap?.ads ?? 0} iklan
    {baseline ? ` · baseline ${rm(baseline.min_myr)}–${rm(baseline.max_myr)} ikut ${baseline.banding || 'CPS unik'}` : ''} · Client close: N/A
  </span>
);

const Decision = ({ ibId, s, reload }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const decide = (status) => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/suggestions/${s.id}/decision`), { status, reason }, auth());
    setRejecting(false);
    reload();
  }, status === 'ditolak' ? 'Cadangan ditolak' : 'Cadangan diterima');
  if (rejecting) {
    return (
      <div className="flex flex-wrap gap-1.5 w-full" data-testid={`mb-sugg-reject-form-${s.id}`}>
        <Input className="h-10 text-sm flex-1 min-w-[180px]" aria-label="Sebab tolak" placeholder="Sebab tolak (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`mb-sugg-reject-reason-${s.id}`} />
        <Button size="sm" variant="outline" className="h-10" disabled={busy || reason.trim().length < 3} onClick={() => decide('ditolak')} data-testid={`mb-sugg-reject-submit-${s.id}`}>Tolak</Button>
        <Button size="sm" variant="ghost" className="h-10" onClick={() => setRejecting(false)}>Batal</Button>
      </div>
    );
  }
  return (
    <>
      {s.status !== 'diterima' && <Button size="sm" variant="outline" className="h-10" disabled={busy} onClick={() => decide('diterima')} data-testid={`mb-sugg-accept-${s.id}`}><Check size={14} className="mr-1" /> Terima</Button>}
      {s.status !== 'ditolak' && !s.brief_workflow_id && <Button size="sm" variant="ghost" className="h-10" onClick={() => setRejecting(true)} data-testid={`mb-sugg-reject-${s.id}`}><X size={14} className="mr-1" /> Tolak</Button>}
    </>
  );
};

const NextStep = ({ s }) => {
  const { openTask, workflows = [] } = useContext(MbContext);
  const picName = usePicName();
  if (s.brief_workflow_id) {
    const w = workflows.find((x) => x.id === s.brief_workflow_id);
    return (
      <div className="text-sm text-emerald-400 flex flex-wrap items-center gap-2" data-testid={`mb-sugg-linked-${s.id}`}>
        <span>Dah jadi brief{w ? ` · ${STAGE_LABEL[w.current_stage || 'brief']} · PIC ${picName(w.current_pic_id)}` : ' (task tak ditemui dalam papan)'}</span>
        {w && openTask && (
          <Button size="sm" variant="outline" className="h-10" onClick={() => openTask(w.id, 'ringkasan')} data-testid={`mb-sugg-open-brief-${s.id}`}>
            Buka brief {w.code || ''} <ExternalLink size={14} className="ml-1" />
          </Button>
        )}
      </div>
    );
  }
  if (s.status === 'ditolak') return <p className="text-sm text-red-400">Ditolak: {s.reject_reason || '—'} ({s.decided_by || '—'})</p>;
  return <p className={`text-sm ${muted}`}>Langkah seterusnya: Kaer semak draf brief, ubah jika perlu, kemudian simpan sebagai brief baharu.</p>;
};

const Details = ({ s }) => (
  <details className="rounded-md border border-[hsl(var(--border))] px-3 py-2" data-testid={`mb-sugg-details-${s.id}`}>
    <summary className={`cursor-pointer text-sm ${primary} min-h-8`}>Lihat alasan & batas data</summary>
    <div className="mt-2 space-y-2">
      <Sec title="Kenapa dicadangkan"><p>{s.why}</p></Sec>
      <div className="grid sm:grid-cols-2 gap-2">
        <Sec title="Apa kekal"><Bullets items={s.keep} /></Sec>
        <Sec title="Apa berubah"><Bullets items={s.change} /></Sec>
      </div>
      <Sec title="Hipotesis & ukuran kejayaan"><p>{s.hypothesis}</p><p>Ukuran: {s.success_metric}</p></Sec>
      <Sec title="Penghasilan"><p>{PATH_LABEL[s.production_path]} — {s.production_reason}</p></Sec>
      <Sec title="Batas data"><Bullets items={s.evidence_limits} /></Sec>
      <p className={`text-xs ${muted}`}>Cadangan AI ({s.model}, {s.prompt_version}) — draf untuk semakan Kaer. Bukan bukti punca, bukan label winner.</p>
    </div>
  </details>
);

export const SuggestionCard = ({ ibId, s, canBrief, reload }) => {
  const { openDraft } = useContext(MbContext);
  const angle = s.brief_draft?.angle;
  const change = (s.change || [])[0];
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] p-3 space-y-2 bg-white/[0.02]" data-testid={`mb-sugg-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_CLS[s.status]}`} data-testid={`mb-sugg-status-${s.id}`}>{SUGG_STATUS[s.status]}</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-[#F77F00]/15 text-[#F77F00]">{TYPE_LABEL[s.type]}</span>
        <span className={`text-sm font-semibold ${primary} basis-full sm:basis-0 sm:flex-1 min-w-0`} data-testid={`mb-sugg-title-${s.id}`}>{s.title}</span>
        <span className={`text-xs ${muted}`}>dari {s.version_label}</span>
      </div>
      <div className={`text-sm ${muted} space-y-0.5`}>
        {angle && <p><span className={primary}>Angle:</span> {angle}</p>}
        {s.hook_draft && <p><span className={primary}>Hook cadangan:</span> <span className="italic">{`"${s.hook_draft}"`}</span></p>}
        {change && <p><span className={primary}>Satu unsur berubah:</span> {change}</p>}
        <p className="line-clamp-2" data-testid={`mb-sugg-why-${s.id}`}><span className={primary}>Sebab:</span> {s.why}</p>
        <p className="text-xs"><span className={primary}>Bukti (dikira backend):</span> <Evidence snap={s.snapshot} baseline={s.baseline} /></p>
      </div>
      <NextStep s={s} />
      <Details s={s} />
      {canBrief && (
        <div className="flex flex-wrap gap-1.5">
          {!s.brief_workflow_id && s.status !== 'ditolak' && (
            <Button size="sm" className="h-10" onClick={() => openDraft(s)} data-testid={`mb-sugg-draft-${s.id}`}>
              <FilePlus2 size={14} className="mr-1" /> Semak draf brief
            </Button>
          )}
          <Decision ibId={ibId} s={s} reload={reload} />
        </div>
      )}
    </div>
  );
};
